import { useMemo, createContext, useState, useCallback, useEffect, useContext, useLayoutEffect } from 'react';
import { jsx } from '@emotion/react/jsx-runtime';
import { debounce } from '@myrealtrip/modash';
import Cookies from 'js-cookie';

var APP_VERSION_COOKIE_NAME = 'app-version';
var IS_SSR = typeof window === 'undefined';
var BASE_WEBVIEW_INFO = {
    isWebView: false,
    platform: null,
    service: null,
    version: null,
};

/**
 * 주어진 userAgent로 모바일 여부를 감지합니다.
 * userAgent가 제공되지 않으면 navigator.userAgent를 자동으로 사용합니다.
 *
 * @param userAgent - 사용자 에이전트 문자열 (선택사항)
 * @returns 모바일 여부
 */
function detectMobile(userAgent) {
    // userAgent가 제공되지 않으면 navigator.userAgent 사용
    var targetUserAgent = userAgent !== null && userAgent !== void 0 ? userAgent : (typeof navigator !== 'undefined' ? navigator.userAgent : '');
    var _a = detectPlatform(targetUserAgent), isDefaultMobile = _a.isDefaultMobile, isAndroid = _a.isAndroid, isIos = _a.isIos, isOpera = _a.isOpera, isWindows = _a.isWindows;
    // User Agent 패턴으로 모바일 감지
    var isMobileByUserAgent = isDefaultMobile || isAndroid || isIos || isOpera || isWindows;
    return isMobileByUserAgent;
}
/**
 * 주어진 userAgent로 특정 플랫폼을 감지합니다.
 * userAgent가 제공되지 않으면 navigator.userAgent를 자동으로 사용합니다.
 */
function detectPlatform(userAgent) {
    var targetUserAgent = userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
    var isDefaultMobile = Boolean(targetUserAgent.match(/Mobi/i));
    var isAndroid = Boolean(targetUserAgent.match(/Android/i));
    var isIos = Boolean(targetUserAgent.match(/iPhone|iPad|iPod/i));
    var isOpera = Boolean(targetUserAgent.match(/Opera Mini/i));
    var isWindows = Boolean(targetUserAgent.match(/IEMobile/i));
    var isSSR = Boolean(targetUserAgent.match(/SSR/i));
    return {
        isDefaultMobile: isDefaultMobile,
        isAndroid: isAndroid,
        isIos: isIos,
        isOpera: isOpera,
        isWindows: isWindows,
        isSSR: isSSR,
    };
}
/**
 * 클라이언트에서 User Agent 정보를 수집하고 모바일 여부를 감지합니다.
 * navigator.userAgentData API를 우선 사용하고, 실패 시 기존 userAgent를 사용합니다.
 */
function getUserAgent() {
    if (typeof window === 'undefined') {
        return {
            userAgent: 'SSR',
            mobile: false,
        };
    }
    var navigator = window.navigator;
    var userAgent = navigator.userAgent;
    return {
        userAgent: userAgent,
        mobile: detectMobile(userAgent),
    };
}
function getWebViewInfo(appVersion) {
    if (typeof appVersion !== 'string') {
        return {
            platform: null,
            service: null,
            version: null,
        };
    }
    // app-version 포맷은 {platform}/{service};{version}와 같다.
    // 따라서 '/' 와 ';'로 파싱한다.
    // ex) android/traveler;7.12.0.0
    var _a = appVersion.split(/[/|;\s]+/), platform = _a[0], service = _a[1], version = _a[2];
    return {
        platform: platform,
        service: service,
        version: version,
    };
}
function getDeviceType(userAgent) {
    if (!userAgent && IS_SSR)
        return null;
    var ua = userAgent || (navigator === null || navigator === void 0 ? void 0 : navigator.userAgent);
    var platform = detectPlatform(ua);
    // 모바일이 아닌 경우 데스크톱
    if (!detectMobile(ua)) {
        return 'desktop';
    }
    // 모바일인 경우, 태블릿인지 모바일인지 구분
    // iPad는 태블릿으로 분류
    if (platform.isIos && ua.includes('iPad')) {
        return 'tablet';
    }
    // 안드로이드의 경우 화면 크기나 User Agent로 태블릿 구분
    if (platform.isAndroid && ua.includes('Mobile')) {
        return 'mobile';
    }
    // 기본적으로 모바일로 분류
    return 'mobile';
}

/**
 * 클라이언트에서 모바일 여부를 감지하는 React Hook입니다.
 * SSR 환경에서는 'SSR'을 userAgent로 사용합니다.
 *
 * @returns 모바일 감지 결과 객체
 */
function useMobileDetect() {
    var userAgent = useMemo(function () {
        if (typeof navigator === 'undefined') {
            return 'SSR';
        }
        return navigator.userAgent;
    }, []);
    var platform = useMemo(function () { return detectPlatform(userAgent); }, [userAgent]);
    var isMobile = useMemo(function () {
        // SSR 환경에서는 false 반환
        if (userAgent === 'SSR') {
            return false;
        }
        return detectMobile(userAgent);
    }, [userAgent, platform]);
    var isDesktop = useMemo(function () {
        if (userAgent === 'SSR') {
            return false;
        }
        return !isMobile && !platform.isSSR;
    }, [userAgent, isMobile, platform]);
    return {
        isMobile: isMobile,
        isDesktop: isDesktop,
        isAndroid: platform.isAndroid,
        isIos: platform.isIos,
        isSSR: platform.isSSR,
    };
}

var BreakPoint = {
    MOBILE: 768,
    TABLET: 1100,
};
var DetermineMobileContext = createContext(false);
function DetermineMobileProvider(_a) {
    var _b = _a.defaultMobile, defaultMobile = _b === void 0 ? false : _b, children = _a.children;
    var _c = useState(Boolean(defaultMobile)), isMobile = _c[0], setMobile = _c[1];
    var handleResize = useCallback(function () {
        var current = window.innerWidth < BreakPoint.MOBILE || getUserAgent().mobile;
        setMobile(current);
    }, []);
    var debounceResize = useCallback(debounce(function () { return handleResize(); }, 100), []);
    useEffect(function () {
        handleResize();
        window.addEventListener('resize', debounceResize);
        return function () { return window.removeEventListener('resize', debounceResize); };
    }, [handleResize, debounceResize]);
    return (jsx(DetermineMobileContext.Provider, { value: isMobile, children: children }));
}

function useDetermineMobile() {
    return useContext(DetermineMobileContext);
}

var useIsomorphicLayoutEffect = IS_SSR ? useEffect : useLayoutEffect;

var WebViewContext = createContext(BASE_WEBVIEW_INFO);
function WebViewProvider(_a) {
    var children = _a.children, defaultWebViewInfo = _a.defaultWebViewInfo;
    var _b = useState(defaultWebViewInfo !== null && defaultWebViewInfo !== void 0 ? defaultWebViewInfo : BASE_WEBVIEW_INFO), webViewInfo = _b[0], setWebViewInfo = _b[1];
    useIsomorphicLayoutEffect(function () {
        if (!defaultWebViewInfo) {
            var clientAppVersion = Cookies.get(APP_VERSION_COOKIE_NAME) || null;
            if (!clientAppVersion)
                setWebViewInfo(BASE_WEBVIEW_INFO);
            var _a = getWebViewInfo(clientAppVersion), platform = _a.platform, service = _a.service, version = _a.version;
            setWebViewInfo({
                isWebView: !!clientAppVersion,
                platform: platform,
                service: service,
                version: version,
            });
        }
    }, [defaultWebViewInfo]);
    useIsomorphicLayoutEffect(function () {
        if (webViewInfo.isWebView) {
            window.MRT_WEBLOG_EXEC_MODE = 'WEBVIEW';
        }
    }, [webViewInfo.isWebView]);
    return jsx(WebViewContext.Provider, { value: webViewInfo, children: children });
}

function useWebView() {
    return useContext(WebViewContext);
}

var DeviceIdentifierContext = createContext({
    isMobile: false,
    isMobileWeb: false,
    deviceType: null,
    isWebView: false,
    webViewInfo: BASE_WEBVIEW_INFO,
});
function DeviceIdentifierContextProvider(_a) {
    var children = _a.children, defaultUserAgent = _a.defaultUserAgent;
    var isMobile = useDetermineMobile();
    var webViewInfo = useWebView();
    var deviceType = getDeviceType(defaultUserAgent);
    return (jsx(DeviceIdentifierContext.Provider, { value: {
            isMobile: isMobile,
            isMobileWeb: deviceType === 'mobile' && !(webViewInfo === null || webViewInfo === void 0 ? void 0 : webViewInfo.isWebView),
            deviceType: deviceType,
            isWebView: !!(webViewInfo === null || webViewInfo === void 0 ? void 0 : webViewInfo.isWebView),
            webViewInfo: webViewInfo,
        }, children: children }));
}
function DeviceIdentifierProvider(_a) {
    var children = _a.children, defaultWebViewInfo = _a.defaultWebViewInfo, defaultUserAgent = _a.defaultUserAgent;
    return (jsx(WebViewProvider, { defaultWebViewInfo: defaultWebViewInfo, children: jsx(DetermineMobileProvider, { children: jsx(DeviceIdentifierContextProvider, { defaultUserAgent: defaultUserAgent, children: children }) }) }));
}

function useDeviceIdentifier() {
    return useContext(DeviceIdentifierContext);
}

export { BreakPoint, DetermineMobileContext, DetermineMobileProvider, DeviceIdentifierContext, DeviceIdentifierProvider, WebViewContext, WebViewProvider, detectMobile, detectPlatform, getUserAgent, useDetermineMobile, useDeviceIdentifier, useMobileDetect, useWebView };
